import React, { useMemo, useState } from 'react';
import { Download, Scale, Search, SlidersHorizontal } from 'lucide-react';
import { Select } from '../../components/Controls';
import { download, toCSV } from '../../format';
import { Empty, num, useFrozen } from './shared';
import { AdjustModal, yearsAround } from './BalanceHub';

// Leave Balance Overview (Stephanet's leavemgmt/Balances.jsx): Total (entitlement + carried + adjusted), Used and Balance per employee
// for the chosen leave types and year, with manual adjustments

const DEFAULT_COLS = ['Annual', 'Sick', 'Medical', 'Casual', 'Study'];

export default function Balances({ data, reload, year, setYear, tick }) {
  const tracked = data.types.filter(t => t.has_balance && t.active !== false).map(t => t.name);
  const [cols, setCols] = useState(() => DEFAULT_COLS.filter(c => tracked.includes(c)));
  const [q, setQ] = useState('');
  const [dept, setDept] = useState('');
  const [adjusting, setAdjusting] = useState(null);
  const rows = useMemo(() => data.employees.filter(e => (!dept || e.department === dept)
    && (!q || [e.name, e.employee_no].some(v => String(v || '').toLowerCase().includes(q.toLowerCase())))), [data, q, dept]);
  const ref = useFrozen(2, [tick, rows.length, cols.length]);
  const cell = (e, t) => {
    const b = e.balances[t];
    if (!b) return [null, null, null];
    return [b.entitled + b.carried + b.adjusted, b.used + b.monetized, b.remaining];
  };
  const toggle = t => setCols(c => (c.includes(t) ? c.filter(x => x !== t) : [...c, t]));
  const exportCsv = () => download(`leave-balances-${year}.csv`, toCSV([
    { label: 'Employee ID', get: e => e.employee_no }, { label: 'Employee', get: e => e.name }, { label: 'Department', get: e => e.department },
    ...cols.flatMap(t => [{ label: `${t} Total`, get: e => cell(e, t)[0] ?? '' }, { label: `${t} Used`, get: e => cell(e, t)[1] ?? '' }, { label: `${t} Balance`, get: e => cell(e, t)[2] ?? '' }]),
  ], rows));
  return (
    <>
      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Leave Balance Overview <span className="mdl-year">{year}</span></b>
          <div className="att2-filters no-print">
            <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Search employee..." value={q} onChange={e => setQ(e.target.value)} /></div>
            <Select value={dept} onChange={e => setDept(e.target.value)}><option value="">All Departments</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select>
            <Select value={year} onChange={e => setYear(Number(e.target.value))}>{yearsAround(data.year).map(y => <option key={y} value={y}>{y}</option>)}</Select>
            {data.can.export && <button className="mdr-btn" onClick={exportCsv}><Download size={13} /> Export</button>}
          </div>
        </div>
        <div className="lvm-colpick no-print">
          <SlidersHorizontal size={13} /> <span>Leave types shown:</span>
          {tracked.map(t => <button key={t} className={cols.includes(t) ? 'on' : ''} onClick={() => toggle(t)}>{t}</button>)}
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table lvm-frozen lvm-f2 lvm-bal" ref={ref}>
            <thead>
              <tr>
                {data.can.edit && <th rowSpan={2} className="c no-print">Action</th>}
                <th rowSpan={2}>Employee</th>
                {cols.map(t => <th key={t} colSpan={3} className="c lvm-grp">{t.toUpperCase()}</th>)}
              </tr>
              <tr>{cols.map(t => ['Tot', 'Used', 'Bal'].map(s => <th key={t + s} className="c lvm-sub">{s}</th>))}</tr>
            </thead>
            <tbody key={tick}>
              {rows.map((e, i) => (
                <tr key={e.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  {data.can.edit && <td className="c no-print"><button className="mds-view" onClick={() => setAdjusting(e)}><Scale size={12} /> Adjust</button></td>}
                  <td className="lvm-emp"><b>{e.name}</b><small>{e.employee_no} • {e.department || '—'}</small></td>
                  {cols.map(t => {
                    const [tot, used, rem] = cell(e, t);
                    return tot === null
                      ? <td key={t} colSpan={3} className="c lvm-na">n/a</td>
                      : [<td key={`${t}a`} className="c">{num(tot)}</td>, <td key={`${t}b`} className="c am">{num(used)}</td>,
                        <td key={`${t}c`} className={`c b ${rem <= 0 ? 'rd' : rem <= 3 ? 'am' : 'g'}`}>{num(rem)}</td>];
                  })}
                </tr>
              ))}
              {!rows.length && <Empty cols={cols.length * 3 + 2}>No employees match.</Empty>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot att2-foot"><span>Tot = entitlement + carried forward + adjustments · Used includes monetized days · pending requests are not deducted until approved</span></div>
      </div>

      {data.adjustments.length > 0 && (
        <div className="mdr-card mdr-table-card">
          <div className="mds-table-head"><b>Balance Adjustments & Carry Forward</b></div>
          <div className="mdr-scroll lvm-short">
            <table className="mdr-table mds-table">
              <thead><tr><th>Date</th><th>Employee</th><th>Year</th><th>Leave Type</th><th>Type</th><th className="c">Days</th><th>Reason</th></tr></thead>
              <tbody>{data.adjustments.map(a => (
                <tr key={a.id} className="mdr-row"><td className="nowrap">{(a.created || '').slice(0, 10)}</td><td>{a.name}</td><td>{a.leave_year}</td><td>{a.leave_type}</td>
                  <td>{a.adjustment_type}</td><td className={`c b ${a.days < 0 ? 'rd' : 'g'}`}>{a.days > 0 ? '+' : ''}{num(a.days)}</td><td>{a.reason}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
      {adjusting && <AdjustModal emp={adjusting} data={data} year={year} onClose={() => setAdjusting(null)} onDone={() => { setAdjusting(null); reload(); }} />}
    </>
  );
}
